/**
 * M7 S37 part 3: where a secured part came from (the integration notes' `origin` at `runtime.onPart → addComponent`).
 * The M2 bridge (`features/bridgeMining`) sets the entered system's salvage site (`systemWorld.ts salvageSiteOf`)
 * and the home cluster's Harrow POI on every system entry. Only the derelict's sections are at the site: their part
 * keys start with `h` (`space/salvage.ts`: `h<seed36>:<sectionId>:<index>`); combat, death and contract wreck parts
 * (`c`, `d`, `k`) have no site. The story link reads the same origin for `partSecured`'s POI.
 *
 * M7 S37d: the Mission 2 barge wreck (`story-link/bargeWreck.ts`) is a story wreck keyed `BARGE_WRECK_KEY`; its parts
 * (`s:m2-barge:<index>`) report the story's own barge POI (`BARGE_POI_ID`), so the story can tell them apart. It is
 * not a derelict: it never counts as a First Rush ruin.
 *
 * Module state, set on system entry only; `partOrigin` allocates nothing.
 */
import { NO_ORIGIN, type CoreOrigin } from '../story/hooks'

/** The Mission 2 barge wreck's salvage key (its parts are `<key>:<index>`). */
export const BARGE_WRECK_KEY = 's:m2-barge'
/** The origin POI the barge wreck's parts report (a story POI: no galaxy POI exists for it). */
export const BARGE_POI_ID = 'story:m2-barge'

const BARGE_PART_PREFIX = `${BARGE_WRECK_KEY}:`
const BARGE_ORIGIN: CoreOrigin = Object.freeze({ poiId: BARGE_POI_ID, harrowPoiId: null })

let site: CoreOrigin = NO_ORIGIN

/** The entered system's salvage site POI (null: none) and the home cluster's Harrow POI (null outside it). */
export function setSalvageSite(poiId: string | null, harrowPoiId: string | null): void {
  site = poiId === null && harrowPoiId === null ? NO_ORIGIN : { poiId, harrowPoiId }
}

/** The site of a secured part by its key: the salvage site for a derelict section's part, the barge, else none. */
export function partOrigin(partKey: string): CoreOrigin {
  if (partKey.charCodeAt(0) === 0x68 /* h */) return site
  return partKey.startsWith(BARGE_PART_PREFIX) ? BARGE_ORIGIN : NO_ORIGIN
}
